/**
 * Build the "SKJV It is Written Report" markdown emitted by the
 * report dialog. Pure function — no React, no DOM access beyond an
 * optional origin override for tests.
 *
 * The shape is consumed by `brain/plan/NEXT_TRAINING.md` (paste-and-go
 * "for next training") and by the get-commentary-feedback skill.
 */

import { parseReference } from '@skjv/common';
import { buildMetadataLines, type ReportRunMeta } from './report-metadata';

export interface ReportCrossRef {
  ref: string;
  text?: string;
}

export interface ReportInput {
  reference: string;
  verseText: string;
  pericope: {
    title: string;
    book: string;
    chapter: number;
    startVerse: number;
    endVerse: number;
    summary: string | null;
  } | null;
  crossRefs: ReportCrossRef[];
  variantSummary: string | null;
  aiResponse: string;
  /** The prompt the run actually sent to the model, when the server
   *  reported it. Absent on recalled or history entries generated before
   *  the field existed. */
  prompt?: string | null;
  /** What the run cost and what the guards/validator said — printed as the
   *  report's first section. */
  meta?: ReportRunMeta;
  /** The reader's "Any thoughts?" seed notes. An INPUT to the run, so the
   *  report keeps them verbatim beside the prompt: commentary that chases a
   *  reader's idea off the passage is a finding about the seed, not the
   *  model. */
  readerThoughts?: string | null;
}

function defaultOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://skjv.ai';
}

export function buildVerseUrl(reference: string, origin = defaultOrigin()): string | null {
  const loc = parseReference(reference);
  if (!loc) return null;
  const { testamentIndex, bookIndex, chapterIndex, verseIndex } = loc;
  return `${origin}/#${testamentIndex}-${bookIndex}-${chapterIndex}-${verseIndex}`;
}

export function buildReportMarkdown(
  input: ReportInput,
  origin = defaultOrigin(),
  reportedAt = new Date().toISOString(),
): string {
  const lines: string[] = [];

  lines.push(`# SKJV It is Written Report - ${input.reference}`);
  lines.push('');

  // First, because every other section is read in its light.
  lines.push('## Metadata');
  lines.push(...buildMetadataLines(input.meta ?? {}, reportedAt));
  lines.push('');

  lines.push('## Verse');
  lines.push(`${input.verseText.trim()} (${input.reference})`);
  lines.push('');

  lines.push('## Pericope');
  if (input.pericope) {
    const { title, book, chapter, startVerse, endVerse, summary } = input.pericope;
    lines.push(`${title} (${book} ${chapter}:${startVerse}-${endVerse})`);
    if (summary) lines.push(summary);
  } else {
    lines.push('_None._');
  }
  lines.push('');

  lines.push('## Cross References');
  if (input.crossRefs.length === 0) {
    lines.push('_None._');
  } else {
    for (const cr of input.crossRefs) {
      const url = buildVerseUrl(cr.ref, origin);
      const label = url ? `[${cr.ref}](${url})` : cr.ref;
      lines.push(cr.text ? `- ${label} — ${cr.text.trim()}` : `- ${label}`);
    }
  }
  lines.push('');

  lines.push('## Textual Variants');
  lines.push(input.variantSummary?.trim() || '_None._');
  lines.push('');

  lines.push('## Reader Thoughts');
  lines.push(input.readerThoughts?.trim() || '_None._');
  lines.push('');

  lines.push('## Prompt');
  if (input.prompt?.trim()) {
    // Fenced: the prompt carries its own headings and blank lines, which
    // would otherwise reflow into the report's own structure.
    lines.push('```text');
    lines.push(input.prompt.trim());
    lines.push('```');
  } else {
    lines.push('_Not captured — generated before the prompt was reported._');
  }
  lines.push('');

  lines.push('## AI Response');
  lines.push(input.aiResponse.trim() || '_(empty)_');

  return lines.join('\n');
}
